import { useMemo } from "react";
import { usePlatform } from "../../usePlatform/usePlatform";

export type InputMode = "decimal" | "numeric" | "text";

/**
 * Custom hook that determines the optimal inputMode attribute for number fields based on platform and requirements.
 *
 * The inputMode attribute tells the device what type of software keyboard to show.
 * There are inconsistencies in what each software keyboard includes across different platforms:
 * - iPhone lacks a minus sign in both numeric and decimal keyboards
 * - Android numeric keyboard has both decimal point and minus sign
 * - iPad has different keyboard behavior than iPhone
 *
 * @param hasDecimals - Whether the number field allows decimal values
 * @param hasNegative - Whether the number field allows negative values
 * @returns The optimal inputMode attribute to use on the number field
 *
 * @example
 * const inputMode = useNumberFieldInputMode({
 *   hasDecimals: true,
 *   hasNegative: false
 * });
 */
export const useNumberFieldInputMode = ({
  hasDecimals,
  hasNegative,
}: {
  hasDecimals: boolean;
  hasNegative: boolean;
}) => {
  const platform = usePlatform();

  const inputMode: InputMode = useMemo(() => {
    let value: InputMode = "decimal";

    if (platform.isIPhone) {
      // iPhone only. iPad has a different software keyboard.
      if (hasNegative) {
        // iPhone lacks a minus sign in both numeric and decimal. So we use text.
        value = "text";
      } else if (hasDecimals) {
        value = "decimal";
      }
    } else if (platform.isAndroid) {
      if (hasNegative) {
        // Android numeric has both a decimal point and a minus sign so it's safe to use in all cases.
        value = "numeric";
      } else if (hasDecimals) {
        // If we don't have negative numbers, we can use decimal (which does not have a minus sign).
        value = "decimal";
      }
    }

    return value;
  }, [hasDecimals, hasNegative, platform.isAndroid, platform.isIPhone]);

  return inputMode;
};
